/**
 * @module app.cm
 */

import { autoinject, bindingMode } from 'aurelia-framework';
import { bindable } from 'aurelia-typed-observable-plugin';
import { ComponentBase, Sorting, SortDirection, IForm } from 'app/shared/lib/essette-site';
import { IAssessmentsConfig } from 'app/core/models/essette-interfaces';
import { AssessmentService } from 'app/core/services/assessment-service';
import { apiCall } from 'app/shared/lib/api/api';
import { IQueryLoadOptions } from 'app/shared/lib/api/api-types';
import { EssPagerCustomElement } from 'app/shared/elements/ess-pager';
import { SortChangeEvent } from 'app/shared/attributes/sortable-attribute';
import { openMemberSummary } from 'app/cd/components/summary/member/member-summary';
import { openCaseSummary } from 'app/cm/components/summary/case-summary';
import { validate } from 'app/shared/elements/ess-rule';
import { IPopup } from 'app/shared/elements/popup-base';
import { FormSuccessCustomEvent } from 'app/shared/elements/forms/ess-form';
import { getApiSetting } from 'app/shared/lib/api/api-settings';
import { HouseholdCasePageContext } from 'app/cm/lib/household-case-page-context';
import { replaceCopyrightText } from 'app/ext/mcg-informed/lib/utilities';
import * as Core from 'app/core/models/essette-api';
import * as CM from 'app/cm/models/essette-api';

@autoinject()
export class AssessmentAttachments extends ComponentBase {

    ////****************** Fields and Properties ************************************////

    @bindable.stringNull({ defaultBindingMode: bindingMode.toView, changeHandler: 'reload' })
    public householdCaseId: string | null = null;

    @bindable.objectNull({ defaultBindingMode: bindingMode.toView })
    public config: IAssessmentsConfig | null = null;

    protected attachments: ReadonlyArray<CM.CaseAssessmentAttachment> = [];

    protected showMemberSummaryLink: boolean = false;
    protected showCaseSummaryLink: boolean = false;
    protected hasCaseRoute = false;
    protected openMemberSummary = openMemberSummary;
    protected openCaseSummary = openCaseSummary;

    private sorting: Sorting = new Sorting();
    protected resultCount: number = 0;
    protected pageSize: number = 20;

    protected validate = validate;
    protected isValid: boolean = false;

    protected mcgCopyright: string | undefined;

    ////****************** Element References ***************************************////

    protected $pager!: EssPagerCustomElement;
    protected $modal!: IPopup;
    protected $form!: IForm;
    protected $assessmentsForm!: Element;

    ////****************** Lifecycle events (in order of execution) *****************////

    constructor(
        private readonly service: AssessmentService,
        private readonly context: HouseholdCasePageContext,
        ...rest: any[]) {
        super(...rest);
        this.checkPermissions();
    }

    protected bind(): void {
        if(!this.householdCaseId) { throw new Error('Bindable property [household-case-id] was not set'); }
        if(!this.config) { throw new Error('Bindable property [config] was not set'); }

        // Set generalized copyright text
        const copyright = getApiSetting(this.context.settings, 'mcg.informed.copyright.$display')?.toString();
        const edition = getApiSetting(this.context.settings, 'mcg.informed.edition.$display')?.toString();
        if(copyright) this.mcgCopyright = replaceCopyrightText(copyright, edition);

        this.service.configure(this.config);
        this.loadAttachments();
    }

    ////****************** Events ***************************************************////

    /** Handles the grid's sort event */
    protected applySort(event: SortChangeEvent): void {
        const { property, direction } = event.detail;
        const { sorting } = this;
        if(direction === null) {
            sorting.remove(property);
        } else {
            sorting.add({
                reference: property,
                direction: direction === 'asc' ? SortDirection.Ascending : SortDirection.Descending
            });
        }
        this.reload();
    }

    protected formSuccess({ detail }: FormSuccessCustomEvent): void {
        this.$modal.close();
        if(detail.form && detail.form.reset) detail.form.reset();
        this.reload();
    }

    ////****************** Functions ************************************************////

    public async reload(): Promise<void> {
        await this.loadAttachments();
    }

    private async checkPermissions(): Promise<void> {
        await this.accountContext.load();
        this.hasCaseRoute = this.accountContext.hasRouteAccess('cm.case');
        this.showMemberSummaryLink = this.accountContext.hasRouteAccess('cd.member.summary');
        this.showCaseSummaryLink = this.accountContext.hasRouteAccess('cm.case.summary');
    }

    protected addAttachments(): void {
        if(!this.householdCaseId) { throw new Error('Missing required property: householdCaseId'); }
        this.$modal.open();
    }

    protected modalClose(): void {
        if(this.$form.reset) this.$form.reset();
        this.$modal.close();
    }

    ////****************** Service Calls ********************************************////

    @apiCall
    public async loadAttachments(): Promise<void> {
        if(!this.householdCaseId) return;

        const options: Partial<IQueryLoadOptions> = {
            top: this.pageSize,
            page: this.$pager?.page || 0,
            sorting: this.sorting,
            includeInactive: false
        };

        const includes: string[] = [
            'task',
            'metadata.modified.byUser',
            'lastSession',
            'lastSession.metadata.modified.byUser',
            'case',
            'member'
        ];

        try {
            this.state.beginLoad();
            const query = this.service.createAttachmentsByTargetQuery(this.householdCaseId, options, includes);
            query.filterOr((f) => {
                f.filterIsNull('task.id');
                f.filterBoolean('task.isActive', true);
            });
            const response = await this.service.get<Core.AssessmentAttachment>(query);
            this.attachments = response.results;
            this.resultCount = response.count || 0;
        }
        catch(error) {
            throw error;
        }
        finally {
            this.state.endLoad();
        }
    }
}
